import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Disclaimer } from "@/components/Shared";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import {
  Briefcase, Sparkles, ArrowRight, CheckCircle2, Building2, GraduationCap, Globe,
  Heart, ShieldCheck, Plane, Users, AlertTriangle, ListChecks, Crown, Award,
  Stethoscope, HardHat, ChefHat, Code, Calculator, Wrench, Megaphone, Cpu,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STREAMS = [
  {
    badge: "Most common", title: "Core Skills Stream", icon: Briefcase,
    desc: "For occupations on the Core Skills Occupation List (CSOL). The mainstream pathway for skilled professionals.",
    bullets: [
      "Nominated by an approved Australian employer",
      "At least 1 year of relevant work experience",
      "Meet English language requirements",
      "Paid at or above the Core Skills Income Threshold (or market salary — whichever is higher)",
    ],
    accent: "from-[#29324A] to-[#1f2638]",
  },
  {
    badge: "High earners", title: "Specialist Skills Stream", icon: Crown,
    desc: "For highly skilled professionals in specialised, higher-income positions. A streamlined pathway aimed at top global talent.",
    bullets: [
      "Senior technology professionals",
      "Cyber security specialists",
      "Engineering specialists",
      "Executive management roles",
      "Advanced healthcare professionals",
    ],
    accent: "from-[#C43A89] to-[#C43A89]",
  },
  {
    badge: "Industry agreements", title: "Labour Agreement Stream", icon: Award,
    desc: "Where an Australian employer has entered into a formal Labour Agreement with the Government. Used in industries with significant workforce shortages.",
    bullets: ["Aged care", "Hospitality", "Agriculture", "Regional businesses", "Healthcare"],
    accent: "from-[#1B1430] to-[#29324A]",
  },
];

const ELIGIBILITY = [
  { icon: Building2, title: "Approved sponsor",            body: "An Australian business approved to sponsor overseas workers." },
  { icon: Briefcase, title: "Nominated position",          body: "Employer must nominate a genuine position aligned to your occupation." },
  { icon: ListChecks, title: "Skills & experience",        body: "Generally at least 1 year of relevant work experience in your occupation." },
  { icon: Globe, title: "English language",                body: "English requirements apply unless an exemption is available." },
  { icon: ShieldCheck, title: "Health & character",        body: "Health examinations and police clearances may be required." },
];

const BENEFITS = [
  { icon: Briefcase,  title: "Work in Australia",          body: "Live and work for an approved Australian employer." },
  { icon: Heart,      title: "Bring your family",          body: "Eligible family members can usually be included in the application." },
  { icon: Plane,      title: "Travel freely",              body: "Travel in and out of Australia while the visa remains valid." },
  { icon: Award,      title: "Pathway to PR",              body: "Many holders may become eligible for ENS (Subclass 186) and other PR pathways." },
  { icon: ShieldCheck, title: "Greater flexibility",       body: "Recent reforms provide more time to find a new sponsor if employment ends." },
];

const OCCUPATIONS = [
  { icon: Code,        l: "Software Engineer" },
  { icon: Code,        l: "Developer Programmer" },
  { icon: Stethoscope, l: "Registered Nurse" },
  { icon: Calculator,  l: "Accountant" },
  { icon: HardHat,     l: "Civil Engineer" },
  { icon: HardHat,     l: "Mechanical Engineer" },
  { icon: Wrench,      l: "Electrician" },
  { icon: ChefHat,     l: "Chef" },
  { icon: GraduationCap, l: "Early Childhood Teacher" },
  { icon: HardHat,     l: "Construction Project Manager" },
  { icon: Cpu,         l: "ICT Business Analyst" },
  { icon: Megaphone,   l: "Marketing Specialist" },
];

const PROCESS = [
  { n: "1", t: "Employer Sponsorship Approval", d: "The Australian employer applies to become an approved sponsor." },
  { n: "2", t: "Position Nomination",            d: "The employer nominates a genuine position aligned to your occupation." },
  { n: "3", t: "Visa Application",               d: "You lodge the SID 482 visa application with supporting evidence." },
  { n: "4", t: "Health & Character Checks",      d: "Medical examinations and police clearances are completed." },
  { n: "5", t: "Visa Decision",                  d: "Department of Home Affairs assesses and decides the visa application." },
];

const YEARS_OPTS = ["Less than 1 year", "1–2 years", "3–5 years", "6–10 years", "10+ years"];
const ENGLISH_OPTS = [
  { v: "functional", l: "Functional (IELTS 4.5)" },
  { v: "competent",  l: "Competent (IELTS 6.0 / PTE 50)" },
  { v: "proficient", l: "Proficient (IELTS 7.0 / PTE 65)" },
  { v: "superior",   l: "Superior (IELTS 8.0 / PTE 79)" },
  { v: "exempt",     l: "Exempt / passport from English-speaking country" },
  { v: "none",       l: "No test taken yet" },
];
const QUAL_OPTS = ["Trade certificate", "Diploma", "Bachelor degree", "Master degree", "Doctorate", "Other"];
const SPONSOR_OPTS = [
  { v: "yes",       l: "Yes — I have an Australian sponsor" },
  { v: "no",        l: "No — I'm looking for sponsorship" },
  { v: "exploring", l: "Exploring — not sure yet" },
];
const STREAM_OPTS = [
  { v: "core",             l: "Core Skills Stream" },
  { v: "specialist",       l: "Specialist Skills Stream" },
  { v: "labour_agreement", l: "Labour Agreement Stream" },
  { v: "unsure",           l: "Not sure — please advise" },
];
const YN  = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const YNU = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unsure", l: "Not sure" }];

export default function SkillsInDemand482() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", nationality: "", current_location: "",
    current_visa_status: "", occupation: "", anzsco: "", years_experience: "",
    english_level: "", english_test: "", qualification: "", has_sponsor: "",
    sponsor_name: "", stream_preference: "core", salary_range: "",
    preferred_state: "", include_family: "no", interest_pr: "unsure", notes: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const required = ["full_name", "email", "phone", "nationality", "current_location", "occupation", "years_experience", "english_level", "qualification", "has_sponsor", "stream_preference", "include_family", "interest_pr"];

  const submit = async (e) => {
    e.preventDefault();
    for (const k of required) {
      if (!form[k]) { toast.error(`Please complete: ${k.replace(/_/g, " ")}`); return; }
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error("Please enter a valid email."); return; }
    if (!consent) { toast.error("Please tick the consent checkbox."); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/sid482/submit`, { ...form, consent: true, consent_timestamp: new Date().toISOString() });
      setDone(r.data);
      toast.success("Assessment received — our team will be in touch.");
      const el = document.getElementById("assessment-form");
      if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="sid482-page" className="bg-white">

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Briefcase className="h-3 w-3" /> Skills in Demand · Subclass 482 · 2026
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight mt-4 max-w-3xl">
            Work in Australia through <span className="text-[#C43A89]">employer sponsorship</span>.
          </h1>
          <p className="text-[15px] md:text-base text-slate-200 leading-relaxed mt-5 max-w-2xl">
            The Skills in Demand (SID) Visa is Australia's leading employer-sponsored temporary work visa. Introduced in December 2024 to replace the TSS, it offers a modern framework with clearer pathways to permanent residency.
          </p>
          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            {[
              { v: "Up to 4 years", l: "Visa duration" },
              { v: "3 streams", l: "Core · Specialist · LA" },
              { v: "Family OK", l: "Bring dependants" },
              { v: "PR pathway", l: "via Subclass 186" },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 backdrop-blur p-3.5">
                <div className="font-display text-lg md:text-xl text-white">{s.v}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#C43A89] mt-1">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#assessment-form" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 btn-shimmer transition-colors">
              Start Free 482 Assessment <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <Link to="/book-consultation" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors">
              Book a Consultation <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT IS IT + BENEFITS */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-5">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">What is the SID Visa?</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">
              Sponsored work, with a pathway to permanent residency.
            </h2>
            <p className="text-[14px] text-slate-600 leading-relaxed mt-4">
              The Skills in Demand Visa (Subclass 482) allows Australian employers to sponsor overseas workers for positions they cannot fill locally — designed to address workforce shortages while offering clearer routes to PR.
            </p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-3">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white border border-slate-200 hover:border-[#C43A89] hover:shadow-[0_18px_40px_-15px_rgba(196,58,137,0.2)] transition-all p-5 relative overflow-hidden">
                <span aria-hidden className="absolute top-0 left-0 h-1 w-12 bg-gradient-to-r from-[#29324A] to-[#C43A89]" />
                <b.icon className="h-6 w-6 text-[#C43A89]" />
                <h3 className="font-display text-base text-[#29324A] mt-3 leading-snug">{b.title}</h3>
                <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1.5">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STREAMS */}
      <section className="py-16 md:py-20 bg-[#F8F4FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">SID Visa Streams</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">Three streams. One visa.</h2>
            <p className="text-[14px] text-slate-600 leading-relaxed mt-4">Your occupation, income level and employer relationship determine which stream applies.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4 md:gap-5">
            {STREAMS.map((s, i) => (
              <article key={i} className="bg-white border border-slate-200 hover:border-[#C43A89] hover:shadow-[0_22px_50px_-20px_rgba(196,58,137,0.25)] transition-all p-7 relative overflow-hidden">
                <span aria-hidden className={`absolute top-0 left-0 h-1 w-20 bg-gradient-to-r ${s.accent}`} />
                <div className="flex items-center justify-between">
                  <s.icon className="h-8 w-8 text-[#29324A]" />
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C43A89] bg-[#C43A89]/10 px-2 py-1">{s.badge}</span>
                </div>
                <h3 className="font-display text-xl text-[#29324A] mt-4 leading-snug">{s.title}</h3>
                <p className="text-[13.5px] text-slate-700 leading-relaxed mt-3">{s.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-[12.5px] text-slate-700">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Key Eligibility</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">What you'll need to qualify.</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {ELIGIBILITY.map((e, i) => (
              <div key={i} className="bg-white border border-slate-200 p-5 hover:border-[#29324A] transition-colors">
                <e.icon className="h-7 w-7 text-[#29324A]" />
                <h3 className="font-display text-base text-[#29324A] mt-3 leading-snug">{e.title}</h3>
                <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1.5">{e.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OCCUPATIONS */}
      <section className="py-16 md:py-20 bg-[#FDF2F8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Common Occupations</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">Hundreds of occupations on the Core Skills List.</h2>
            <p className="text-[13.5px] text-slate-600 leading-relaxed mt-3">Popular roles include — but eligibility depends on the current list and individual circumstances.</p>
          </div>
          <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {OCCUPATIONS.map((o, i) => (
              <div key={i} className="bg-white border border-slate-200 px-4 py-3 flex items-center gap-3 hover:border-[#C43A89] transition-colors">
                <o.icon className="h-4 w-4 text-[#C43A89] shrink-0" />
                <span className="text-[13px] text-[#29324A]">{o.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-20 bg-[#29324A] text-white relative overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">SID Visa Process</span>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mt-3">From sponsorship to visa decision — in 5 steps.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-5 gap-3 md:gap-4">
            {PROCESS.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 p-5 relative">
                <div className="absolute -top-4 left-5 bg-[#C43A89] text-white font-display text-lg w-8 h-8 flex items-center justify-center">{s.n}</div>
                <h3 className="font-display text-base mt-3 leading-tight">{s.t}</h3>
                <p className="text-[12px] text-slate-300 leading-relaxed mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS THIS VISA FOR + WHY PRO ADVICE */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-5 md:gap-7">
          <div className="bg-[#F8F4FB] border border-slate-200 p-7">
            <Users className="h-7 w-7 text-[#29324A]" />
            <h3 className="font-display text-2xl text-[#29324A] mt-3">Who is the 482 for?</h3>
            <ul className="mt-3 space-y-2">
              {[
                "Skilled workers overseas",
                "International graduates in Australia",
                "Temporary visa holders already onshore",
                "Professionals seeking employer sponsorship",
                "Workers eyeing a potential pathway to PR",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-rose-50 border border-rose-200 p-7">
            <AlertTriangle className="h-7 w-7 text-rose-600" />
            <h3 className="font-display text-2xl text-rose-900 mt-3">Why professional advice matters</h3>
            <p className="text-[13.5px] text-rose-900 leading-relaxed mt-3">
              Employer-sponsored visas involve complex requirements for both employers and applicants — occupation eligibility, salary thresholds, work experience, sponsorship obligations and PR pathways must all be assessed carefully before lodging.
            </p>
            <p className="text-[12.5px] text-rose-800 mt-3 italic">Obtaining professional advice early may help identify issues and ensure the right pathway is explored.</p>
          </div>
        </div>
      </section>

      {/* ASSESSMENT FORM */}
      <section id="assessment-form" className="py-16 md:py-24 bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white relative overflow-hidden grain">
        <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase"><Sparkles className="h-3 w-3" /> Free 482 Assessment</span>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mt-3">Discover if SID 482 could be your pathway.</h2>
            <p className="text-[14px] text-slate-200 leading-relaxed mt-4">Complete our free online assessment — our migration team will review your occupation eligibility, sponsorship options, visa requirements, family member eligibility and potential PR pathways.</p>
          </div>

          {done ? (
            <div data-testid="sid-result" className="mt-10 bg-white text-slate-900 p-8 md:p-10">
              <span aria-hidden className="block h-1 w-24 bg-gradient-to-r from-[#29324A] to-[#C43A89] mb-5" />
              <div className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Thank you, {form.full_name.split(" ")[0]}</div>
              <h3 className="font-display text-3xl text-[#29324A] mt-2 leading-tight">Initial stream suggestion</h3>
              <div className="mt-5 bg-[#F8F4FB] border-l-4 border-[#29324A] p-5">
                <div className="text-[11px] tracking-wider uppercase text-[#29324A] font-bold mb-1">Recommended starting point</div>
                <div className="font-display text-xl text-[#29324A]">{done.recommended_pathway}</div>
              </div>
              <p className="text-[13.5px] text-slate-700 leading-relaxed mt-5">
                A member of our migration team will review your responses and contact you within 1 business day with next steps — occupation eligibility, sponsorship options and any documentation we need to progress.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/book-consultation" className="bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-5 py-3 inline-flex items-center gap-1.5">Book consultation <ArrowRight className="h-3.5 w-3.5" /></Link>
                <button onClick={() => { setDone(null); setForm({ ...form, full_name: "", email: "", phone: "" }); }} className="bg-white border border-slate-300 hover:border-[#29324A] text-slate-700 text-xs font-bold tracking-wider uppercase px-5 py-3">Submit another</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 bg-white text-slate-900 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]">
              <div className="p-6 md:p-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <F label="Full name *"><Input data-testid="sid-name" required value={form.full_name} onChange={(e) => u("full_name", e.target.value)} /></F>
                  <F label="Email *"><Input data-testid="sid-email" type="email" required value={form.email} onChange={(e) => u("email", e.target.value)} /></F>
                  <F label="Phone *"><Input data-testid="sid-phone" required value={form.phone} onChange={(e) => u("phone", e.target.value)} /></F>
                  <F label="Nationality *"><Input data-testid="sid-nationality" required value={form.nationality} onChange={(e) => u("nationality", e.target.value)} placeholder="e.g. India · Philippines · UK" /></F>
                  <F label="Currently in… *">
                    <Select value={form.current_location} onValueChange={(v) => u("current_location", v)}>
                      <SelectTrigger data-testid="sid-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_australia">In Australia</SelectItem>
                        <SelectItem value="outside_australia">Outside Australia</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Current visa status (if any)"><Input data-testid="sid-current-visa" value={form.current_visa_status} onChange={(e) => u("current_visa_status", e.target.value)} placeholder="e.g. 485 · 500 · 600" /></F>
                  <F label="Occupation *"><Input data-testid="sid-occupation" required value={form.occupation} onChange={(e) => u("occupation", e.target.value)} placeholder="e.g. Software Engineer" /></F>
                  <F label="ANZSCO code (optional)"><Input data-testid="sid-anzsco" value={form.anzsco} onChange={(e) => u("anzsco", e.target.value)} placeholder="e.g. 261313" /></F>
                  <F label="Years of relevant experience *">
                    <Select value={form.years_experience} onValueChange={(v) => u("years_experience", v)}>
                      <SelectTrigger data-testid="sid-years"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{YEARS_OPTS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="English language level *">
                    <Select value={form.english_level} onValueChange={(v) => u("english_level", v)}>
                      <SelectTrigger data-testid="sid-english"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{ENGLISH_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Highest qualification *">
                    <Select value={form.qualification} onValueChange={(v) => u("qualification", v)}>
                      <SelectTrigger data-testid="sid-qual"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{QUAL_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Do you have an Australian sponsor? *">
                    <Select value={form.has_sponsor} onValueChange={(v) => u("has_sponsor", v)}>
                      <SelectTrigger data-testid="sid-sponsor"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{SPONSOR_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Sponsor name (if you have one)"><Input data-testid="sid-sponsor-name" value={form.sponsor_name} onChange={(e) => u("sponsor_name", e.target.value)} placeholder="e.g. ABC Engineering Pty Ltd" /></F>
                  <F label="Preferred SID stream *">
                    <Select value={form.stream_preference} onValueChange={(v) => u("stream_preference", v)}>
                      <SelectTrigger data-testid="sid-stream"><SelectValue /></SelectTrigger>
                      <SelectContent>{STREAM_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Annual salary range (optional)"><Input data-testid="sid-salary" value={form.salary_range} onChange={(e) => u("salary_range", e.target.value)} placeholder="e.g. AUD 90k–110k" /></F>
                  <F label="Preferred Australian state (optional)"><Input data-testid="sid-state" value={form.preferred_state} onChange={(e) => u("preferred_state", e.target.value)} placeholder="e.g. NSW · VIC · QLD" /></F>
                  <F label="Bring family members? *">
                    <Select value={form.include_family} onValueChange={(v) => u("include_family", v)}>
                      <SelectTrigger data-testid="sid-family"><SelectValue /></SelectTrigger>
                      <SelectContent>{YN.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Interested in PR pathway later? *">
                    <Select value={form.interest_pr} onValueChange={(v) => u("interest_pr", v)}>
                      <SelectTrigger data-testid="sid-pr"><SelectValue /></SelectTrigger>
                      <SelectContent>{YNU.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>

                <F label="Anything else our team should know (optional)">
                  <Textarea data-testid="sid-notes" rows={3} value={form.notes} onChange={(e) => u("notes", e.target.value)} placeholder="Share any additional context — refusal history, timing constraints, specific role offered, etc." />
                </F>

                <ConsentCheckbox
                  value={consent}
                  onChange={setConsent}
                  testid="sid-consent"
                  label="I understand this form provides general information only and does not guarantee visa eligibility or visa grant. I consent to Shamima Yasmin Anwar contacting me about my enquiry."
                />

                <Button data-testid="sid-submit" type="submit" disabled={submitting || !consent} className="w-full bg-gradient-to-br from-[#29324A] to-[#C43A89] hover:from-[#1f2638] hover:to-[#a22b6e] text-white rounded-none py-5 font-semibold text-[15px] btn-shimmer disabled:opacity-50">
                  {submitting ? "Submitting…" : "Start My Free Assessment"}
                </Button>

                <p className="text-[11px] text-slate-500 leading-relaxed flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-[#C43A89] mt-0.5 shrink-0" />
                  General information only. Eligibility requirements, occupation lists, salary thresholds and visa criteria may change. Individual circumstances must be assessed against current legislation and policy before lodging.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#FDF2F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-display text-2xl md:text-3xl text-[#29324A] leading-tight">Ready to discuss your sponsorship pathway?</h3>
          <p className="text-[14px] text-slate-700 leading-relaxed mt-3 max-w-2xl mx-auto">
            Book a 30-minute consultation with one of our Registered Migration Agents (MARN-registered) — we'll review your occupation, employer sponsorship options and the most appropriate SID stream for your circumstances.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/book-consultation" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 btn-shimmer transition-colors">
              Book a 482 Consultation <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <a href="#assessment-form" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#29324A] text-slate-700 text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors">
              Start Free Assessment <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      <Disclaimer />
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold tracking-wider text-slate-600 uppercase mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
